export const resetPassword = {
  extends: 'Page',
  metadata: { title: 'Reset password / Symbols' },
  flow: 'y',
  align: 'center center',
  padding: 'D C',
  width: '100%',
  minHeight: '100dvh',
  maxWidth: 'none',

  onInit: (el, s) => {
    const url = new URL(window.location.href)
    s.token = url.searchParams.get('token')
    s.email = url.searchParams.get('email') || ''
  },

  ResetCard: {
    flow: 'y',
    gap: 'C',
    padding: 'D',
    width: '100%',
    maxWidth: 'I',
    round: 'C',
    background: 'overlay',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'line',
    boxShadow: '0 24px 60px -24px var(--color-shadow)',

    Head: {
      flow: 'y',
      gap: 'A',
      Eyebrow: {
        tag: 'span',
        fontSize: 'Z2',
        fontWeight: '700',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: 'blue',
        text: 'Account'
      },
      Title: {
        tag: 'h1',
        fontSize: 'D',
        fontWeight: '800',
        color: 'title',
        letterSpacing: '-0.02em',
        text: 'Reset password'
      },
      Sub: {
        tag: 'p',
        color: 'caption',
        fontSize: 'A',
        // Changes label based on whether a reset token is present in the URL.
        text: (el, s) => s.token
          ? 'Enter your new password below.'
          : 'Enter your email to receive a reset link.'
      }
    },

    // Request-reset form — shown when no token is present.
    RequestForm: {
      tag: 'form',
      flow: 'y',
      gap: 'A',
      hide: (el, s) => !!s.token,

      onSubmit: async (ev, el, s) => {
        ev.preventDefault()
        const root = el.node
        const email = (root.querySelector('input[name="email"]')?.value || '').trim().toLowerCase()
        const errBox = root.querySelector('[data-lg-err]')
        if (errBox) errBox.textContent = ''
        if (!email) {
          if (errBox) errBox.textContent = 'Email is required.'
          s.update({ touched: true })
          return
        }
        s.update({ loading: true })
        try {
          await el.sdk.requestPasswordReset(email)
          s.update({ sent: true, loading: false })
        } catch (e) {
          s.update({ loading: false })
          if (errBox) errBox.textContent = e?.message || String(e)
          el.call('openNotification', { title: 'Error message', message: `${e}`, type: 'error' })
        }
      },

      Email: {
        tag: 'input',
        width: '100%',
        padding: 'A B',
        round: 'C',
        background: 'overlayStrong',
        border: 'none',
        outline: 'none',
        color: 'title',
        fontSize: 'A',
        boxSizing: 'border-box',
        type: 'email',
        name: 'email',
        placeholder: 'name@symbols.app',
        required: 'true',
        autocomplete: 'email'
      },

      Error: { tag: 'span', 'data-lg-err': 'true', fontSize: 'Z2', color: 'red', minHeight: 'A' },

      Submit: {
        tag: 'button',
        type: 'submit',
        width: '100%',
        padding: 'A B',
        round: 'C',
        background: 'blue',
        color: 'white',
        fontSize: 'A',
        fontWeight: '700',
        border: 'none',
        cursor: 'pointer',
        text: 'Send reset link'
      }
    },

    // Sent confirmation message — shown after successful request-reset.
    SentMessage: {
      flow: 'y',
      gap: 'A',
      hide: (el, s) => !s.sent,
      Notice: {
        tag: 'p',
        color: 'caption',
        fontSize: 'A',
        text: (el, s) => `Check your inbox at ${s.email || 'your email'}. Follow the link to set a new password.`
      }
    },

    // New-password form — shown when a token is present in the URL.
    NewPasswordForm: {
      tag: 'form',
      flow: 'y',
      gap: 'A',
      hide: (el, s) => !s.token,

      onSubmit: async (ev, el, s) => {
        ev.preventDefault()
        const root = el.node
        const password = root.querySelector('input[name="password"]')?.value || ''
        const repeatPassword = root.querySelector('input[name="repeatPassword"]')?.value || ''
        const errBox = root.querySelector('[data-lg-err]')
        if (errBox) errBox.textContent = ''
        if (password !== repeatPassword) {
          if (errBox) errBox.textContent = 'Passwords do not match.'
          s.update({ repeatPasswordTouched: true })
          el.call('openNotification', {
            title: 'Failed to change password',
            message: 'Passwords do not match',
            type: 'error'
          })
          return
        }
        s.update({ loading: true })
        try {
          await el.sdk.confirmPasswordReset(s.token, password)
          el.call('openNotification', {
            title: 'Success',
            message: 'Password has been successfully changed',
            type: 'success'
          })
          setTimeout(() => el.router('/signin', el.getRoot(), {}), 200)
        } catch (e) {
          s.update({ loading: false })
          if (errBox) errBox.textContent = e?.message || String(e)
          el.call('openNotification', { title: 'Error message', message: `${e}`, type: 'error' })
        }
      },

      NewPassword: {
        tag: 'input',
        width: '100%',
        padding: 'A B',
        round: 'C',
        background: 'overlayStrong',
        border: 'none',
        outline: 'none',
        color: 'title',
        fontSize: 'A',
        boxSizing: 'border-box',
        type: 'password',
        name: 'password',
        placeholder: 'New password',
        required: 'true',
        autocomplete: 'new-password'
      },
      RepeatPassword: {
        tag: 'input',
        width: '100%',
        padding: 'A B',
        round: 'C',
        background: 'overlayStrong',
        border: 'none',
        outline: 'none',
        color: 'title',
        fontSize: 'A',
        boxSizing: 'border-box',
        type: 'password',
        name: 'repeatPassword',
        placeholder: 'Repeat new password',
        required: 'true',
        autocomplete: 'new-password'
      },

      Error: { tag: 'span', 'data-lg-err': 'true', fontSize: 'Z2', color: 'red', minHeight: 'A' },

      Submit: {
        tag: 'button',
        type: 'submit',
        width: '100%',
        padding: 'A B',
        round: 'C',
        background: 'blue',
        color: 'white',
        fontSize: 'A',
        fontWeight: '700',
        border: 'none',
        cursor: 'pointer',
        text: 'Set new password'
      }
    },

    Footer: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'caption',
      align: 'center',
      text: 'Remember your password?',
      SigninLink: {
        tag: 'a',
        href: '/signin',
        fontSize: 'Z2',
        color: 'blue',
        marginLeft: 'Y',
        text: 'Sign in'
      }
    }
  }
}
